import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  User, Mail, Phone, Globe, Calendar, LogOut, Loader2, Sparkles, MessageSquare,
  Bell, Save, Lock, Send, IdCard, Edit, Check, Home, Wallet, CalendarClock, Receipt,
  HelpCircle, Camera, Trash2, Brain, Gamepad2,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Avatar } from "@/components/Avatar";
import { PushToggle } from "@/components/PushPermissionBanner";
import { BookableAppointmentsList } from "@/components/AppointmentBookingModal";
import { ClientPaymentRequests } from "@/components/PaymentRequestModal";
import { ChatCard } from "@/components/chat/ChatMiniApps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyAccountPage() {
  const { user, logout, authHeader, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["profile", "messages", "notifs", "apt", "pays"];
  const initialTab = searchParams.get("tab");
  const currentTab = validTabs.includes(initialTab) ? initialTab : "profile";
  const [form, setForm] = useState({});
  const [messages, setMessages] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [plans, setPlans] = useState([]);
  const [profileRequests, setProfileRequests] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [hasPic, setHasPic] = useState(false);
  const [picBump, setPicBump] = useState(0);
  const [memory, setMemory] = useState(null);
  const [memoryLoaded, setMemoryLoaded] = useState(false);

  // Fetch spiritual memory once when memory tab is opened
  useEffect(() => {
    if (currentTab !== "memory" || memoryLoaded) return;
    axios.get(`${API}/users/spiritual-profile/me`, { headers: authHeader() })
      .then((r) => setMemory(r.data?.profile || null))
      .catch(() => setMemory(null))
      .finally(() => setMemoryLoaded(true));
  }, [currentTab, memoryLoaded, authHeader]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/users/me/extra`, { headers: authHeader() })
      .then((r) => setHasPic(!!r.data.has_profile_pic))
      .catch(() => null);
  }, [user, authHeader, picBump]);

  const onUploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Imagen muy grande (máx 2MB)");
    if (!file.type.startsWith("image/")) return toast.error("Sólo imágenes");
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const b64 = (reader.result || "").toString().split(",")[1] || "";
        await axios.post(`${API}/users/me/profile-pic`, { data_b64: b64, mime_type: file.type }, { headers: authHeader() });
        toast.success("Foto de perfil actualizada");
        setHasPic(true);
        try { window.__bumpAvatarCache?.(user.id); } catch { /* noop */ }
        setPicBump((n) => n + 1);
      };
      reader.readAsDataURL(file);
    } catch { toast.error("Error subiendo foto"); }
  };

  const onDeleteAvatar = async () => {
    if (!window.confirm("¿Eliminar tu foto de perfil?")) return;
    try {
      await axios.delete(`${API}/users/me/profile-pic`, { headers: authHeader() });
      toast.success("Foto eliminada");
      setHasPic(false);
      try { window.__bumpAvatarCache?.(user.id); } catch { /* noop */ }
      setPicBump((n) => n + 1);
    } catch { toast.error("Error"); }
  };

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", email: user.email || "",
      phone: user.phone || "", country: user.country || "", age: user.age || "",
    });
  }, [user]);

  const load = useCallback(async () => {
    try {
      const [m, n, a, p, pr] = await Promise.all([
        axios.get(`${API}/users/messages`, { headers: authHeader() }),
        axios.get(`${API}/users/notifications`, { headers: authHeader() }),
        axios.get(`${API}/users/appointments`, { headers: authHeader() }),
        axios.get(`${API}/users/payment-plans`, { headers: authHeader() }),
        axios.get(`${API}/users/profile-requests`, { headers: authHeader() }),
      ]);
      setMessages(m.data || []);
      setNotifs(n.data || []);
      setAppointments(a.data || []);
      setPlans(p.data || []);
      setProfileRequests(pr.data || []);
    } catch (e) {
      if (e?.response?.status === 401) { logout(); navigate("/ingresar"); }
    }
  }, [authHeader, logout, navigate]);

  useEffect(() => { if (user) load(); }, [user, load]);

  // Real-time updates via SSE
  useRealtimeEvents({
    notification: () => load(),
    message: () => load(),
  });

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const r = await axios.patch(`${API}/users/me`, { ...form, age: Number(form.age) || undefined }, { headers: authHeader() });
      if (r.data?.pending) {
        toast.success("Solicitud enviada al admin. Recibirás una notificación cuando se apruebe.");
      } else {
        await refresh();
        toast.success("Perfil actualizado");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSavingProfile(false); }
  };

  const confirmAttendance = async (aid) => {
    try {
      await axios.post(`${API}/users/appointments/${aid}/confirm`, {}, { headers: authHeader() });
      toast.success("Asistencia confirmada. ¡Gracias!");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const onUploadReceipt = async (planId, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Máximo 5MB");
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const b64 = (reader.result || "").toString().split(",")[1] || "";
          await axios.post(`${API}/users/payment-plans/${planId}/receipts`,
            { filename: file.name, mime_type: file.type, data_b64: b64 },
            { headers: authHeader() });
          toast.success("Comprobante enviado. Pendiente de revisión.");
          load();
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Error subiendo");
        } finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch { setUploading(false); toast.error("Error leyendo archivo"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users/me/password`, pwForm, { headers: authHeader() });
      toast.success("Contraseña actualizada");
      setPwForm({ current_password: "", new_password: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMsg.trim()) return;
    setSendingMsg(true);
    try {
      await axios.post(`${API}/users/messages`, { body: newMsg }, { headers: authHeader() });
      setNewMsg("");
      toast.success("Mensaje enviado a Cristopher");
      load();
    } catch (err) { toast.error("Error"); } finally { setSendingMsg(false); }
  };

  const markRead = async (nid) => {
    try {
      await axios.patch(`${API}/users/notifications/${nid}/read`, {}, { headers: authHeader() });
      setNotifs((ns) => ns.map((n) => n.id === nid ? { ...n, read: true } : n));
    } catch (e) { /* silent */ }
  };

  if (!user) return null;
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 lg:px-12" data-testid="my-account">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl lg:text-4xl text-white">Mi Cuenta</h1>
            <p className="text-sm text-neutral-400 mt-1 flex items-center gap-2">
              <IdCard className="w-3.5 h-3.5 text-[#d4af37]" />
              ID: <span className="font-mono text-[#d4af37]" data-testid="user-visible-id">{user.visible_id}</span> · {user.username}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => window.dispatchEvent(new Event("tutorial:open"))}
              data-testid="open-tutorial-btn"
              className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2"
              title="Ver tutorial otra vez"
            >
              <HelpCircle className="w-3.5 h-3.5" /> Tutorial
            </button>
            <Link to="/" data-testid="back-home-btn" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2">
              <Home className="w-3.5 h-3.5" /> Inicio
            </Link>
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn" className="btn-outline-gold px-4 py-2 rounded-full text-xs uppercase tracking-widest flex items-center gap-2">
              <LogOut className="w-3.5 h-3.5" /> Salir
            </button>
          </div>
        </div>

        {/* Quick navigation tabs to landing sections */}
        <div className="mb-5 flex flex-wrap gap-2" data-testid="my-account-quick-nav">
          {[
            { id: "lecturas",     label: "🔮 LECTURAS",     href: "/#lecturas" },
            { id: "hechiceria",   label: "🕯️ HECHICERÍA",   href: "/#hechiceria" },
            { id: "consulta",     label: "💬 CONSULTA",     href: "/#consulta" },
            { id: "testimonios",  label: "⭐ TESTIMONIOS",  href: "/#testimonios" },
            { id: "pago",         label: "💸 PAGO",         href: "/#pago" },
            { id: "contacto",     label: "📲 CONTACTO",     href: "/#contacto" },
          ].map((t) => (
            <Link
              key={t.id}
              to={t.href}
              data-testid={`nav-${t.id}`}
              className="text-xs px-3 py-1.5 rounded-full border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 hover:scale-105 transition uppercase tracking-widest font-semibold"
            >{t.label}</Link>
          ))}
        </div>

        <Tabs value={currentTab} onValueChange={(v) => setSearchParams({ tab: v })} className="w-full">
          <TabsList className="bg-black/60 border border-white/10 flex flex-wrap h-auto">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><User className="w-3.5 h-3.5 mr-1.5" />Perfil</TabsTrigger>
            <TabsTrigger value="apt" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Calendar className="w-3.5 h-3.5 mr-1.5" />Mis Citas ({appointments.length})</TabsTrigger>
            <TabsTrigger value="pays" className="data-[state=active]:bg-[#d4af37] data-[state=active]:text-black text-neutral-300"><Wallet className="w-3.5 h-3.5 mr-1.5" />Mis Pagos ({plans.length})</TabsTrigger>
            <Link to="/juegos" data-testid="tab-juegos" className="inline-flex items-center px-3 py-1.5 rounded text-xs uppercase tracking-widest text-neutral-300 hover:bg-[#d4af37]/10 hover:text-[#d4af37]">
              <Gamepad2 className="w-3.5 h-3.5 mr-1.5" />Juegos
            </Link>
          </TabsList>

          <TabsContent value="profile" className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
            <form onSubmit={saveProfile} className="glass-dark rounded-2xl p-6 space-y-3">
              <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2"><Edit className="w-4 h-4 text-[#d4af37]" /> Editar Perfil</h3>

              {/* Profile picture block */}
              <div className="flex items-center gap-4 p-3 bg-black/30 border border-white/10 rounded-xl mb-3">
                <Avatar
                  key={picBump}
                  userId={user.id}
                  name={user.name || user.username}
                  authHeader={authHeader}
                  hasPicHint={hasPic}
                  size={64}
                />
                <div className="flex-1">
                  <div className="text-xs text-neutral-300 mb-1.5 font-semibold">Foto de perfil</div>
                  <div className="flex gap-1.5 flex-wrap">
                    <label data-testid="upload-avatar" className="text-[11px] px-2.5 py-1 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 cursor-pointer inline-flex items-center gap-1">
                      <Camera className="w-3 h-3" /> {hasPic ? "Reemplazar" : "Subir foto"}
                      <input type="file" accept="image/*" hidden onChange={(e) => onUploadAvatar(e.target.files?.[0])} />
                    </label>
                    {hasPic && (
                      <button type="button" data-testid="delete-avatar" onClick={onDeleteAvatar} className="text-[11px] px-2.5 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Eliminar
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] text-neutral-500 mt-1">Imagen máx 2MB · se mostrará en tu chat y notificaciones.</p>
                </div>
              </div>

              {/* Notifications toggle */}
              <div className="p-3 bg-black/30 border border-white/10 rounded-xl mb-3">
                <div className="text-xs text-neutral-300 mb-1.5 font-semibold">🔔 Notificaciones del navegador</div>
                <p className="text-[10px] text-neutral-500 mb-2">Recibe avisos instantáneos de mensajes, citas y pagos en este dispositivo (escritorio, Android e iOS 16.4+).</p>
                <PushToggle />
              </div>

              <p className="text-[11px] text-amber-300/80 mb-2">⚠️ Por seguridad, cualquier cambio será revisado y aprobado individualmente por el administrador.</p>
              {profileRequests.filter(r => r.status === "pending").length > 0 && (
                <div data-testid="profile-pending" className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs space-y-1">
                  <div className="text-amber-300 font-semibold flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Cambios pendientes de aprobación:</div>
                  {profileRequests.filter(r => r.status === "pending").map((r) => (
                    <div key={r.id || r.created_at} className="text-amber-200/90">
                      {Object.entries(r.requested_changes || {}).map(([k,v]) => `${k}: "${v}"`).join(" · ")}
                    </div>
                  ))}
                </div>
              )}
              {[
                {k:"name",label:"Nombre",icon:User},{k:"email",label:"Email",icon:Mail,type:"email"},
                {k:"phone",label:"Teléfono",icon:Phone},{k:"country",label:"País",icon:Globe},
                {k:"age",label:"Edad",icon:Calendar,type:"number"},
              ].map(({k,label,icon:Ic,type}) => (
                <div key={k}>
                  <Label className="text-neutral-300 mb-1.5 flex items-center gap-2 text-xs uppercase tracking-widest"><Ic className="w-3 h-3 text-[#d4af37]" />{label}</Label>
                  <Input data-testid={`profile-${k}`} type={type || "text"} value={form[k] || ""} onChange={(e) => setForm({...form, [k]: e.target.value})} className="bg-black/40 border-white/10 text-white" />
                </div>
              ))}
              <button data-testid="save-profile" disabled={savingProfile} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest mt-2 flex items-center justify-center gap-2">{savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" />Guardar</>}</button>
            </form>

            <form onSubmit={changePassword} className="glass-dark rounded-2xl p-6 space-y-3 h-fit">
              <h3 className="font-serif text-xl text-white mb-3 flex items-center gap-2"><Lock className="w-4 h-4 text-[#d4af37]" /> Cambiar Contraseña</h3>
              <Input data-testid="pw-current" type="password" required placeholder="Contraseña actual" value={pwForm.current_password} onChange={(e) => setPwForm({...pwForm, current_password: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              <Input data-testid="pw-new" type="password" required minLength={6} placeholder="Nueva contraseña (mín 6)" value={pwForm.new_password} onChange={(e) => setPwForm({...pwForm, new_password: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              <button data-testid="save-pw" className="btn-outline-gold w-full py-3 rounded-full uppercase text-xs tracking-widest">Actualizar Contraseña</button>
            </form>
          </TabsContent>

          <TabsContent value="messages" className="mt-5">
            <div className="glass-dark rounded-2xl p-6">
              <h3 className="font-serif text-xl text-white mb-4 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#d4af37]" /> Mensajes privados con Cristopher</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {messages.length === 0 ? <p className="text-sm text-neutral-500 italic text-center py-6">Aún no hay mensajes. Envía el primero.</p> : messages.map((m, i) => (
                  <div key={i} className={`flex ${m.from_role === "admin" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.from_role === "admin" ? "bg-black/60 border border-[#d4af37]/30 text-neutral-200 rounded-bl-sm" : "bg-[#d4af37]/15 border border-[#d4af37]/40 text-white rounded-br-sm"}`}>
                      <div className={`text-[10px] uppercase tracking-widest mb-1 ${m.from_role === "admin" ? "text-[#d4af37]" : "text-emerald-400"}`}>{m.from_role === "admin" ? "Cristopher" : "Tú"} · {new Date(m.created_at).toLocaleString()}</div>
                      <div className="whitespace-pre-wrap">{m.body}</div>
                      {Array.isArray(m.cards) && m.cards.length > 0 && (
                        <div className="mt-2 flex flex-col gap-1.5" data-testid={`ai-cards-${m.id || i}`}>
                          {m.cards.map((c) => <ChatCard key={c.type} card={c} msgId={m.id || i} createdAt={m.created_at} viewerRole="user" />)}
                        </div>
                      )}
                      {m.is_ai && <div className="text-[9px] text-[#d4af37]/80 mt-1 italic">✦ Respuesta automática del equipo · Cristopher revisa todo personalmente</div>}
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={sendMessage} className="flex gap-2">
                <Textarea data-testid="msg-input" rows={2} value={newMsg} onChange={(e) => setNewMsg(e.target.value)} placeholder="Escribe tu mensaje privado a Cristopher..." className="bg-black/40 border-white/10 text-white" />
                <button data-testid="msg-send" disabled={sendingMsg || !newMsg.trim()} className="btn-gold rounded-full w-12 h-12 flex items-center justify-center self-end disabled:opacity-50"><Send className="w-4 h-4" /></button>
              </form>
            </div>
          </TabsContent>

          <TabsContent value="notifs" className="mt-5 space-y-2">
            {notifs.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">Sin notificaciones aún.</div> : notifs.map((n) => (
              <button
                key={n.id}
                data-testid={`notif-${n.id}`}
                onClick={() => {
                  if (!n.read) markRead(n.id);
                  if (n.link) navigate(n.link);
                }}
                className={`w-full text-left glass-dark rounded-xl p-4 flex items-start gap-3 hover:bg-black/40 transition ${!n.read ? "border-l-2 border-[#d4af37]" : ""}`}
              >
                <Bell className="w-4 h-4 text-[#d4af37] mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-white text-sm font-semibold">{n.title}</h4>
                    {n.category && (
                      <span className="text-[10px] uppercase tracking-widest text-[#d4af37]/70">{n.category}</span>
                    )}
                  </div>
                  <p className="text-xs text-neutral-300 whitespace-pre-wrap">{n.body}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-[10px] text-neutral-500">{new Date(n.created_at).toLocaleString()}</p>
                    {n.link && <span className="text-[10px] text-[#d4af37]">Abrir →</span>}
                  </div>
                </div>
                {n.read && <Check className="w-3.5 h-3.5 text-emerald-400 mt-1.5" />}
              </button>
            ))}
          </TabsContent>

          <TabsContent value="apt" className="mt-5 space-y-3">
            <BookableAppointmentsList plans={plans} onBooked={() => load()} />
            {appointments.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">No tienes citas agendadas. Cuando liquides un trabajo, aquí aparecerá el botón para agendar.</div> : appointments.map((a) => (
              <div key={a.id} data-testid={`apt-${a.id}`} className="glass-dark rounded-xl p-5 grid grid-cols-1 lg:grid-cols-4 gap-3">
                <div><div className="text-[10px] uppercase tracking-widest text-neutral-500">Servicio</div><div className="text-white">{a.service_name}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-neutral-500">Fecha</div><div className="text-white">{a.scheduled_date}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-neutral-500">Hora</div><div className="text-white">{a.scheduled_time}</div></div>
                <div><div className="text-[10px] uppercase tracking-widest text-neutral-500">Estado</div><div className="text-emerald-400 capitalize">{a.status}</div></div>
                {a.notes && <div className="lg:col-span-4 text-xs text-neutral-400 border-t border-white/5 pt-2">Notas: {a.notes}</div>}
                <div className="lg:col-span-4 flex items-center gap-2 pt-2 border-t border-white/5">
                  {a.attendance_confirmed ? (
                    <span data-testid={`apt-confirmed-${a.id}`} className="text-[11px] flex items-center gap-1 text-emerald-300"><Check className="w-3 h-3" /> Asistencia confirmada</span>
                  ) : a.status === "scheduled" || a.status === "rescheduled" ? (
                    <button data-testid={`apt-confirm-${a.id}`} onClick={() => confirmAttendance(a.id)} className="text-[11px] px-2.5 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1"><Check className="w-3 h-3" /> Confirmar asistencia</button>
                  ) : null}
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="pays" className="mt-5 space-y-3">
            <ClientPaymentRequests authHeader={authHeader} prefillServiceId={searchParams.get("service") || null} />

            {plans.length === 0 ? <div className="glass-dark rounded-2xl p-12 text-center text-neutral-500 italic">No tienes trabajos ni pagos registrados.</div> : plans.map((p) => {
              const effectiveTotal = Number(p.agreed_price_after_offer ?? p.agreed_price ?? 0);
              const remaining = Math.max(0, effectiveTotal - Number(p.paid_total || 0));
              const pct = effectiveTotal > 0 ? Math.min(100, (p.paid_total / effectiveTotal) * 100) : 0;
              const isPaid = p.status === "paid" || p.fully_paid;
              const acceptedPromo = (p.promotions || []).find((pp) => pp.status === "accepted");
              const totalDiscount = Math.max(0, Number(p.agreed_price || 0) - effectiveTotal);
              const fmtP = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })} ${p.currency}`;
              return (
                <div key={p.id} data-testid={`user-plan-${p.id}`} className="glass-dark rounded-xl p-5">
                  <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Trabajo / Servicio</div>
                      <div className="text-white font-medium flex items-center gap-2"><Receipt className="w-4 h-4 text-[#d4af37]" /> {p.service_name}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {acceptedPromo && (
                        <span data-testid={`user-plan-promo-badge-${p.id}`} className="text-[10px] px-2 py-0.5 rounded-full border border-sky-400/50 bg-sky-500/15 text-sky-200">🎁 Liquidado con promoción</span>
                      )}
                      <span data-testid={`user-plan-status-${p.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border ${isPaid ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : p.status === "cancelled" ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                        {isPaid ? "Liquidado" : p.status === "cancelled" ? "Cancelado" : "Activo"}
                      </span>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-emerald-300">Pagado: {fmtP(p.paid_total)}</span>
                      <span className="text-neutral-300">
                        {totalDiscount > 0 && <span className="line-through text-neutral-500 mr-1.5">{fmtP(p.agreed_price)}</span>}
                        Total: <span data-testid={`user-plan-total-${p.id}`}>{fmtP(effectiveTotal)}</span>
                      </span>
                    </div>
                    <div className="w-full h-2.5 rounded-full bg-black/60 border border-white/5 overflow-hidden">
                      <div className={`h-full ${isPaid ? (acceptedPromo ? "bg-sky-400" : "bg-emerald-500") : "bg-[#d4af37]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-[11px] mt-1.5" data-testid={`user-plan-remaining-${p.id}`}>
                      {remaining > 0 ? (
                        <span className="text-amber-300">Te resta por pagar: <span className="font-semibold">{fmtP(remaining)}</span></span>
                      ) : (
                        <span className="text-emerald-300">Saldo restante: <span className="font-semibold">{fmtP(0)}</span> · ¡Liquidado totalmente! Gracias por confiar en Cristopher.</span>
                      )}
                    </div>
                  </div>

                  {acceptedPromo && (
                    <div data-testid={`user-plan-promo-detail-${p.id}`} className="mb-3 rounded-lg border border-sky-400/30 bg-sky-500/10 px-3 py-2 text-[11px]">
                      <div className="text-[10px] uppercase tracking-widest text-sky-300 mb-1">Promoción temporal aplicada</div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1">
                        <span className="text-neutral-400">Precio original: <span className="line-through text-neutral-300">{fmtP(p.agreed_price)}</span></span>
                        <span className="text-neutral-400">Descuento: <b className="text-emerald-300">−{fmtP(acceptedPromo.discount_amount)}</b>{acceptedPromo.kind === "percentage" ? ` (${acceptedPromo.value}%)` : ""}</span>
                        <span className="text-neutral-400">Total con descuento: <b className="text-white">{fmtP(effectiveTotal)}</b></span>
                        {acceptedPromo.accepted_at && <span className="text-neutral-500">Liquidado el {new Date(acceptedPromo.accepted_at).toLocaleDateString("es-MX")}</span>}
                      </div>
                    </div>
                  )}

                  {p.payment_promise_date && p.status === "active" && !isPaid && (
                    <div className="text-[11px] flex items-center gap-1.5 mb-2 text-amber-200">
                      <CalendarClock className="w-3 h-3" />
                      Promesa de pago acordada: <span className="font-semibold">{p.payment_promise_date}</span>
                    </div>
                  )}

                  {p.notes && <div className="text-xs text-neutral-400 italic mb-2">📝 {p.notes}</div>}

                  {p.installments?.length > 0 && (
                    <div className="border-t border-white/10 pt-2 mt-2">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Historial de pagos ({p.installments.length})</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {p.installments.map((i) => (
                          <div key={i.id} data-testid={`user-inst-${i.id}`} className={`flex justify-between items-center text-xs rounded px-2 py-1.5 border ${i.reflected === false ? "bg-amber-500/[0.07] border-amber-500/30" : "bg-black/30 border-white/5"}`}>
                            <div className="min-w-0">
                              <span className="text-emerald-300 font-semibold">{fmtP(i.amount)}</span>
                              {i.method && <span className="text-neutral-400 ml-2">· {i.method}</span>}
                              <span data-testid={`user-inst-reflected-${i.id}`} className={`ml-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${i.reflected === false ? "bg-amber-500/20 text-amber-200 border border-amber-400/40" : "bg-emerald-500/15 text-emerald-300"}`}>
                                {i.reflected === false ? "⏳ Pendiente de reflejarse" : "✓ Reflejado"}
                              </span>
                            </div>
                            <span className="text-[10px] text-neutral-500 whitespace-nowrap ml-2">{new Date(i.at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Receipts */}
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Comprobantes de pago ({p.receipts?.length || 0})</div>
                      {p.status === "active" && (
                        <label data-testid={`upload-receipt-${p.id}`} className="text-[11px] px-2 py-1 rounded border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 cursor-pointer flex items-center gap-1">
                          <Receipt className="w-3 h-3" /> Subir comprobante
                          <input type="file" accept="image/*,application/pdf" hidden disabled={uploading} onChange={(e) => onUploadReceipt(p.id, e.target.files?.[0])} />
                        </label>
                      )}
                    </div>
                    {p.receipts?.length > 0 && (
                      <div className="space-y-1">
                        {p.receipts.map((r) => (
                          <div key={r.id} className="flex justify-between items-center text-xs bg-black/30 border border-white/5 rounded px-2 py-1.5">
                            <div className="truncate max-w-[60%]" title={r.filename}>
                              <span className="text-neutral-200">📎 {r.filename}</span>
                              <div className="text-[10px] text-neutral-500">{new Date(r.uploaded_at).toLocaleDateString()} · subido por {r.uploaded_by_role === "admin" ? "admin" : "tí"}</div>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${r.status === "confirmed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : r.status === "rejected" ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                              {r.status === "confirmed" ? "✅ Confirmado" : r.status === "rejected" ? "❌ Rechazado" : "⏳ Pendiente"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Memory tab — what Cristopher has learned about this user */}
          <TabsContent value="memory" className="mt-5 space-y-4" data-testid="memory-content">
            <div className="glass-dark rounded-2xl p-6">
              <h3 className="font-serif text-xl text-white mb-1 flex items-center gap-2">
                <Brain className="w-5 h-5 text-[#d4af37]" /> Mi memoria con Cristopher
              </h3>
              <p className="text-xs text-neutral-400 mb-4">
                Estos son los datos que Cristopher ha percibido de ti en sus lecturas y los recordará en las próximas sesiones de la Vidente Virtual y de la Lectura Online.
              </p>

              {!memoryLoaded && (
                <div className="flex items-center gap-2 text-sm text-neutral-400">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                </div>
              )}

              {memoryLoaded && !memory && (
                <div className="p-4 rounded-lg bg-black/30 border border-white/10 text-sm text-neutral-400">
                  Aún no he percibido nada de ti. Realiza una sesión en la Vidente Virtual o termina una Lectura Online para que mi memoria empiece a guardar tu energía.
                </div>
              )}

              {memoryLoaded && memory && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Nombre completo</div>
                      <div className="text-sm text-white" data-testid="memory-full-name">{memory.full_name || "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Signo</div>
                      <div className="text-sm text-white" data-testid="memory-zodiac">{memory.zodiac_sign || "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Fecha de nacimiento</div>
                      <div className="text-sm text-white">{memory.birthdate || "—"}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37]">Sesiones</div>
                      <div className="text-sm text-white">{memory.session_count || 0}</div>
                    </div>
                  </div>

                  {Array.isArray(memory.motivos) && memory.motivos.length > 0 && (
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">Motivos de consulta</div>
                      <div className="flex flex-wrap gap-1.5">
                        {memory.motivos.map((m, i) => (
                          <span key={i} className="text-xs px-2 py-1 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 text-fuchsia-200">{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(memory.facts_learned) && memory.facts_learned.length > 0 && (
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">Lo que Cristopher recuerda de ti</div>
                      <ul className="space-y-1 text-sm text-neutral-200">
                        {memory.facts_learned.map((f, i) => (
                          <li key={i} className="flex gap-2"><span className="text-[#d4af37]">•</span><span>{f}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {Array.isArray(memory.predictions_made) && memory.predictions_made.length > 0 && (
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-2">Predicciones que te ha hecho</div>
                      <ul className="space-y-1 text-sm text-neutral-200">
                        {memory.predictions_made.map((p, i) => (
                          <li key={i} className="flex gap-2"><span className="text-[#d4af37]">✦</span><span>{p}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {memory.last_summary && (
                    <div className="p-3 rounded-lg bg-black/30 border border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-[#d4af37] mb-1">Resumen de tu última sesión</div>
                      <p className="text-sm text-neutral-200 italic">&ldquo;{memory.last_summary}&rdquo;</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
